import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, from, map, merge, Observable, scan, Subject } from 'rxjs';
import { EventType, MatrixEvent, RelationType } from 'matrix-js-sdk';
import { SyncService } from '../sync/sync.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChatState } from '../../model/chat-state';
import { ChatAction } from '../../model/chat-action';
import { ChatRoom } from '../../model/chat-room';
import { SessionService } from '../session/session.service';

/**
 * RxJS-Store für Chat-Zustand:
 * aktueller Raum, alle Räume mit Nachrichten, Reactions, Edit-History
 */
@Injectable({ providedIn: 'root' })
/**
 * ChatStore: Zustand des angezeigten Chats.
 * Begründung: Zentraler Bestandteil der RxJS-Architektur (keine AFO).
 */
export class ChatStoreService {
  private readonly sync = inject(SyncService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // Alle Räume mit allen Nachrichten (mit Änderungshistorie) und Reactions.
  // Aus einer ChatMessage kann das zur Anzeige benötigte DisplayItem
  // (DisplayMessage, DisplayInfo) abgeleitet werden.
  private readonly state$ = new BehaviorSubject<ChatState>(ChatState.new());

  private readonly exchangeRoom$ = new Subject<{
    roomId: string;
    room: ChatRoom;
  }>();

  getState$(): Observable<ChatState> {
    return this.state$.asObservable();
  }

  // Feuert bei jeder Änderung des current ChatRoom
  getCurrentChatRoom$(): Observable<ChatRoom | undefined> {
    return this.getState$().pipe(
      map(state => state.getCurrentRoom()),
      distinctUntilChanged()
    );
  }

  getCurrentRoomId$(): Observable<string | undefined> {
    return this.getState$().pipe(
      map(state => state.currentRoomId),
      distinctUntilChanged()
    );
  }

  getStateSync(): ChatState {
    return this.state$.value;
  }

  constructor() {
    // State-Update sowohl nach Timeline-Event als auch nach Raumwechsel.
    // Alle State-Updates müssen wegen des scan-Operators durch diese Pipeline
    // gehen, d. h. state$.next() an anderer Stelle ist _nicht_ zulässig!
    merge(
      this.sync.message$.pipe(map(event => this.classifyEvent(event))),
      this.sync.decrypted$.pipe(map(event => ({ type: 'decrypted' as const, event }))),
      this.sync.getResetStore$().pipe(map(() => this.toResetStoreAction())),
      this.sessionService.getSessionRoomId$().pipe(map(roomId => this.toSetRoomAction(roomId))),
      this.sync.createRoom$.pipe(map(room => this.toCreateRoomAction(room.roomId))),
      this.sync.deleteRoom$.pipe(map(roomId => this.toDeleteRoomAction(roomId))),
      this.exchangeRoom$.pipe(map(({ roomId, room }) => this.toExchangeRoomAction(roomId, room)))
    )
      .pipe(
        filter(action => !!action),
        scan<ChatAction, ChatState>(this.reduce.bind(this), this.state$.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.state$);
  }

  private toSetRoomAction(roomId: string): ChatAction {
    return { type: 'setRoom', roomId };
  }

  toCreateRoomAction(roomId: string): ChatAction {
    return { type: 'createRoom', roomId };
  }

  toDeleteRoomAction(roomId: string): ChatAction {
    return { type: 'deleteRoom', roomId };
  }

  toExchangeRoomAction(roomId: string, room: ChatRoom): ChatAction {
    return { type: 'exchangeRoom', roomId, room };
  }

  private toResetStoreAction(): ChatAction {
    return { type: 'resetStore' };
  }

  private classifyEvent(event: MatrixEvent): ChatAction | undefined {
    let action: ChatAction | undefined;

    switch (event.getType()) {
      case EventType.RoomMember:
        // 1. Neue Nachricht: Änderung am Benutzer-Migliedsstatus
        // wird als DisplayInfo ausgegeben.
        // 2. Neuberechnung von ApiRoom und ModelRoom wg. Membership-Änderung
        // (invite/join/leave)
        action = { type: 'roomMember', event };
        break;
      case EventType.RoomMessage: {
        const relatesTo = event.getContent()['m.relates_to'];
        action =
          relatesTo?.rel_type === RelationType.Replace // 'm.replace'
            ? { type: 'edit', event }
            : { type: 'message', event };
        break;
      }
      case EventType.RoomMessageEncrypted: // Gelöschte (redacted) Nachricht
        // Nicht entschlüsselbare Nachricht
        action = { type: 'message', event };
        break;
      case EventType.Reaction:
        action = { type: 'reaction', event };
        break;
      case EventType.RoomRedaction:
        // Nur Server-bestätigte Redactions (mit Status null und ohne Transaktions-ID) berücksichtigen.
        // Lokale Redactions ignorieren.
        if (event.status === null && !event.getTxnId()) {
          action = { type: 'redaction', event };
        }
        break;
      case EventType.RoomPinnedEvents:
        action = { type: 'pinnedEvents', event };
        break;

      // Diese Events werden bewusst herausgefiltert.
      // Trigger durch andere Events:
      // RoomCreate aus ClientEvent.Room
      // Delete aus ClientEvent.DeleteRoom
      // // RoomName, RoomTopic aus RoomStateEvent.Events
      case EventType.RoomCreate:
      case EventType.RoomName:
      case EventType.RoomTopic:
      case EventType.RoomPowerLevels:
      case EventType.RoomJoinRules:
      case EventType.RoomGuestAccess:
      case EventType.RoomEncryption:
      case EventType.RoomHistoryVisibility:
      case 'de.gematik.tim.room.default.v1':
      case 'de.gematik.tim.room.name':
      case 'de.gematik.tim.room.topic':
        // Expected - keep idle
        break;
      default:
        // Unexpected
        console.warn('classifyEvent: Keine Action für Event', event.getType(), event);
    }

    return action;
  }

  private reduce(state: ChatState, action: ChatAction): ChatState {
    let next: ChatState;

    switch (action?.type) {
      case 'roomMember':
        next = state.reduceByRoomMemberEvent(action.event);
        break;
      case 'message':
        next = state.reduceByNewMessageEvent(action.event);
        break;
      case 'decrypted':
        next = state.reduceByDecryptedEvent(action.event);
        break;
      case 'edit':
        next = state.reduceByEditMessageEvent(action.event);
        break;
      case 'reaction':
        next = state.reduceByReactionEvent(action.event);
        break;
      case 'redaction':
        next = state.reduceByRedactionEvent(action.event);
        break;
      case 'setRoom':
        next = state.reduceByNewCurrentRoomId(action.roomId);
        break;
      case 'createRoom':
        next = state.reduceByCreateRoom(action.roomId);
        break;
      case 'deleteRoom':
        next = state.reduceByDeleteRoom(action.roomId);
        break;
      case 'exchangeRoom':
        next = state.reduceByExchangeRoom(action.roomId, action.room);
        break;
      case 'pinnedEvents':
        // TODO Re-implement pinning mechanism (remove old code in the process).
        // ...
        next = state; // TODO: Change!
        break;
      case 'resetStore':
        next = ChatState.new();
        break;
      default: // Never reached
        next = state;
    }

    return next;
  }

  /****************************************************************************
   * Trigger from outside (to change chat state)
   ****************************************************************************/

  exchangeRoom(roomId: string, room: ChatRoom): void {
    this.exchangeRoom$.next({ roomId, room });
  }

  /****************************************************************************
   * Archivierung
   ****************************************************************************/

  /**
   * Konvertiere Events aus der Timeline eines Matrix-Raums in ChatRoom.
   */
  roomEventsToChatRoom(events: MatrixEvent[]): ChatRoom | undefined {
    const tempState$ = new BehaviorSubject<ChatState>(ChatState.new());
    from(events)
      .pipe(
        map(event => this.classifyEvent(event)),
        filter(action => !!action),
        scan<ChatAction, ChatState>(this.reduce.bind(this), tempState$.value),
        distinctUntilChanged()
      )
      .subscribe(tempState$);

    const roomId = events.length > 0 ? events[0].getRoomId() : undefined;

    return roomId ? tempState$.value.getRoomById(roomId) : undefined;
  }
}
